import {
  Anchor, Badge, Button, Container, Group, Pagination, Stack, Switch,
  Table, Text, Title,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Task #239 (2026-06-02): PersonRef removed — Person column now reads
// server-resolved person_display_name on the AccountPartyOut payload.
// AccountRef retained for the Account column.
// Task #362 (2026-06-09 §3f sweep): trashcan + restore removed from list rows.
// Delete/restore now live exclusively on the account-party detail page Edit mode,
// mirroring the app-wide convention established by Task #245.
import { AccountRef } from '../components/EntityRef';
import { useAccountParties } from '../hooks/useAccountParties';
import { PARTY_ROLE_LABELS } from '../api/types';
import {
  SortableTh, nextSort, sortParam, type SortState,
} from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';

const PAGE_SIZE = 50;
type AccountPartySortKey = 'role' | 'account' | 'person' | 'status';

export function AccountPartiesListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  // P41 (2026-10-15): header sort, server-side across pages; null = oldest first.
  const [sort, setSortState] = useState<SortState<AccountPartySortKey> | null>(null);
  function onSort(k: AccountPartySortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }
  const { data, isLoading, error } = useAccountParties({
    include_deleted: includeDeleted,
    sort: sortParam(sort),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  // Task #362 (§3f sweep): del / restore / confirmDelete removed — list rows
  // no longer trigger destructive actions. See detail page top-right.
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Title order={2}>Account parties</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/account-parties/new')}>
            New role assignment
          </Button>
        </Group>

        <Switch label="Include deleted" checked={includeDeleted}
          onChange={(e) => { setIncludeDeleted(e.currentTarget.checked); setPage(1); }} />

        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {/* P41 (2026-10-15): every header sorts (server-side, across pages). */}
              <SortableTh label="Role" col="role" sort={sort} onSort={onSort} w={200} />
              <SortableTh label="Account" col="account" sort={sort} onSort={onSort} />
              <SortableTh label="Person" col="person" sort={sort} onSort={onSort} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={200} />
              {/* Task #362 (2026-06-09 §3f sweep): Actions column removed —
                  delete/restore live exclusively on the detail page. */}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={4}><Text c="dimmed">Loading…</Text></Table.Td></Table.Tr>}
            {data?.items.length === 0 && (
              <Table.Tr><Table.Td colSpan={4}><Text c="dimmed">No account-party rows.</Text></Table.Td></Table.Tr>
            )}
            {data?.items.map((p) => (
              <Table.Tr key={p.id} style={{ cursor: 'pointer', opacity: p.deleted_at ? 0.5 : 1 }}
                onClick={() => navigate(`/account-parties/${p.id}`)}>
                <Table.Td><Badge variant="light">{PARTY_ROLE_LABELS[p.role] ?? p.role}</Badge></Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <AccountRef id={p.account_id} linkPathPrefix="/accounts" />
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {p.person_display_name ? (
                    <Anchor component={Link} to={`/persons/${p.person_id}`} size="sm">
                      {p.person_display_name}
                    </Anchor>
                  ) : (
                    <Text size="xs" ff="monospace" c="dimmed">{p.person_id.slice(0, 8)}…</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                  {p.deleted_at
                    ? <Badge color="gray" variant="light">Deleted</Badge>
                    : <Badge color="green" variant="light">Active</Badge>}
                  </StatusExplained>
                </Table.Td>
                {/* Task #362 (§3f sweep): per-row trashcan + restore removed.
                    Delete/Undelete live on the account-party detail page. */}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {data && data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data.total} total</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}
